/**
 * VerificationScreen.jsx - Full-Width Responsive Layout
 * 
 * Screen hiển thị câu hỏi verification sau khi AI phân tích sơ bộ
 * UI đơn giản, nhanh gọn để xác nhận các triệu chứng
 */

import { useState, useEffect } from 'react';
import { useApp } from '../hooks/useApp';
import { SCREENS } from '../config/constants';
import AdaptiveQuestionService from '../services/AdaptiveQuestionService';
import { Container, Text, Button, IconButton, ProgressBar, Card } from '../components/ui';
import Icons from '../components/Icons';

function VerificationScreen() {
    const {
        setCurrentScreen,
        goBack,
        analysisData,
        setAnalysisData,
        showToast
    } = useApp();

    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [verificationInfo, setVerificationInfo] = useState(null);

    // Generate questions on mount
    useEffect(() => {
        if (analysisData?.preliminary_analysis) {
            const result = AdaptiveQuestionService.generateVerificationQuestions(
                analysisData.preliminary_analysis
            );

            if (result.skip_verification) {
                // No verification needed, proceed to final analysis
                showToast('Phân tích đã đủ chính xác!', 'success');
                setCurrentScreen(SCREENS.RESULTS);
                return;
            }

            const formattedQuestions = AdaptiveQuestionService.formatQuestionsForUI(result.questions);
            setQuestions(formattedQuestions);
            setVerificationInfo(AdaptiveQuestionService.getVerificationSummary(
                analysisData.preliminary_analysis.suspected_patterns
            ));
        }
    }, [analysisData]);

    const currentQuestion = questions[currentIndex];
    const progress = questions.length > 0 ? ((currentIndex) / questions.length) * 100 : 0;
    const isLastQuestion = currentIndex === questions.length - 1;

    const handleAnswer = (value) => {
        const newAnswers = { ...answers, [currentQuestion.id]: value };
        setAnswers(newAnswers);

        if (isLastQuestion) {
            // Process all answers
            processVerification(newAnswers);
        } else {
            // Next question
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleSkip = () => {
        if (isLastQuestion) {
            processVerification(answers);
        } else {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleBack = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            goBack();
        }
    };

    const processVerification = async (finalAnswers) => {
        setIsProcessing(true);

        try {
            // Process verification answers
            const updatedAnalysis = AdaptiveQuestionService.processVerificationAnswers(
                analysisData.preliminary_analysis,
                finalAnswers
            );

            // Update analysis data with verification results
            setAnalysisData({
                ...analysisData,
                preliminary_analysis: updatedAnalysis,
                verification_completed: true,
                verification_answers: finalAnswers
            });

            showToast('Xác nhận hoàn tất!', 'success');

            // Navigate to results
            setCurrentScreen(SCREENS.RESULTS);
        } catch (error) {
            console.error('Verification error:', error);
            showToast('Có lỗi xảy ra', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    // Loading state
    if (questions.length === 0) {
        return (
            <Container safe className="flex flex-col items-center justify-center min-h-screen px-responsive">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mb-4" />
                <Text variant="caption">Đang chuẩn bị câu hỏi...</Text>
            </Container>
        );
    }

    return (
        <Container safe className="flex flex-col min-h-screen">
            {/* Header - Full Width */}
            <div className="header-fullwidth border-b border-theme">
                <div className="header-content">
                    <IconButton onClick={handleBack}>
                        <Icons.Back />
                    </IconButton>
                    <div className="flex-1 text-center">
                        <Text variant="caption" className="sm:text-sm">
                            Câu hỏi {currentIndex + 1}/{questions.length}
                        </Text>
                    </div>
                    <div className="w-11" /> {/* Spacer */}
                </div>
            </div>

            {/* Progress */}
            <div className="main-content py-2">
                <ProgressBar progress={progress} />
            </div>

            {/* Intro message (only on first question) */}
            {currentIndex === 0 && verificationInfo && (
                <div className="px-responsive py-3">
                    <Card className="bg-primary/10 border-primary/20">
                        <Text variant="caption" className="text-primary">
                            💡 {verificationInfo.message}
                        </Text>
                    </Card>
                </div>
            )}

            {/* Question */}
            <div className="flex-1 flex flex-col justify-center py-8">
                <div className="main-content">
                    <Text variant="h3" className="text-center mb-8">
                        {currentQuestion.text}
                    </Text>

                    {/* Answer options */}
                    <div className="space-y-3">
                        {currentQuestion.options.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handleAnswer(option.value)}
                                disabled={isProcessing}
                                className={`w-full py-4 px-6 rounded-xl text-lg font-medium
                transition-all active:scale-[0.98]
                ${answers[currentQuestion.id] === option.value
                                        ? 'bg-primary text-white'
                                        : 'bg-theme-secondary text-theme-primary border border-theme'
                                    }
                ${isProcessing ? 'opacity-50' : ''}
              `}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Skip button */}
                <div className="px-responsive pb-8">
                    <button
                        onClick={handleSkip}
                        disabled={isProcessing}
                        className="w-full py-3 text-theme-secondary hover:text-theme-primary transition-colors"
                    >
                        {isLastQuestion ? 'Hoàn tất' : 'Bỏ qua →'}
                    </button>
                </div>

                {/* Processing overlay */}
                {isProcessing && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <Card className="p-6 text-center">
                            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                            <Text>Đang xử lý...</Text>
                        </Card>
                    </div>
                )}
            </div>
        </Container>
    );
}

export default VerificationScreen;
